import React, { useEffect, useState } from "react";
import { Button, Input, Label } from "reactstrap";

import { useLazySearchOrderCustomersQuery } from "../api/api";
import { formatCustomerSuggestionMeta } from "./orderPresentation";
import { useDebounce } from "./salesUtils";

export const MIN_CUSTOMER_QUERY_LENGTH = 2;

/**
 * "Emri ose telefoni" lookup for existing customers.
 *
 * It only suggests: choosing a suggestion hands the customer record to the caller,
 * which copies it into the order. It never creates or edits a Customer. Phone
 * matching uses the same normalized key as the Customers page (server-side).
 */
const OrderRecipientLookup = ({ t, onSelect }) => {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 250).trim();
  const [searchCustomers, { data: matches = [], isFetching }] = useLazySearchOrderCustomersQuery();

  const isMeaningful =
    debouncedQuery.length >= MIN_CUSTOMER_QUERY_LENGTH
    && query.trim().length >= MIN_CUSTOMER_QUERY_LENGTH;

  useEffect(() => {
    if (debouncedQuery.length >= MIN_CUSTOMER_QUERY_LENGTH) {
      searchCustomers({ query: debouncedQuery, limit: 5 });
    }
  }, [debouncedQuery, searchCustomers]);

  // Selecting a customer closes the suggestion list.
  const choose = (customer) => {
    onSelect(customer);
    setQuery("");
  };

  return (
    <div className="ow-lookup">
      <Label for="ow-recipient-lookup" className="ow-field-label">
        {t("ordersWizard.recipientLookupLabel")}
      </Label>
      <div className="ow-input-icon">
        <i className="tim-icons icon-zoom-split" aria-hidden="true" />
        <Input
          id="ow-recipient-lookup"
          type="search"
          autoComplete="off"
          placeholder={t("ordersWizard.recipientLookupPlaceholder")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setQuery("");
          }}
        />
      </div>

      {isMeaningful ? (
        <div className="ow-suggestions" role="listbox" aria-label={t("ordersWizard.recipientLookupLabel")}>
          {isFetching ? (
            <p className="ow-suggestions-state">{t("ordersWizard.searching")}</p>
          ) : matches.length ? (
            matches.map((customer) => (
              <div key={customer.id} className="ow-suggestion" role="option" aria-selected="false">
                <span className="ow-suggestion-avatar" aria-hidden="true">
                  <i className="tim-icons icon-single-02" />
                </span>
                <span className="ow-suggestion-text">
                  <span className="ow-suggestion-name">{customer.recipientName}</span>
                  <span className="ow-suggestion-meta">{formatCustomerSuggestionMeta(customer, t)}</span>
                </span>
                <Button size="sm" color="primary" className="ow-suggestion-use" onClick={() => choose(customer)}>
                  {t("ordersUi.useCustomer")}
                </Button>
              </div>
            ))
          ) : (
            <p className="ow-suggestions-state">{t("ordersUi.noCustomerFound")}</p>
          )}
        </div>
      ) : (
        <p className="ow-field-hint">{t("ordersWizard.recipientLookupHint")}</p>
      )}
    </div>
  );
};

export default OrderRecipientLookup;
